/**
 * Auth Resource — Stytch-hosted auth surface.
 */

import type { HttpLayer } from '../http.js';
import type { RevokeSessionRequest, OIDCConfig } from '../types/index.js';

export class AuthResource {
  constructor(private http: HttpLayer) {}

  async revokeSession(params: RevokeSessionRequest = {}): Promise<void> {
    return this.http.post('/v1/auth/logout', params);
  }

  /**
   * Activate a claim-less Stytch session — injects server-set account_id + user_id.
   * POST /v1/auth/session/activate (auth class 1).
   */
  async activateSession(params?: {
    account_id?: string;
    session_token?: string;
    session_duration_minutes?: number;
  }): Promise<ActivatedSessionResponse> {
    return this.http.post('/v1/auth/session/activate', params ?? {});
  }

  /**
   * Renew an activated session from the opaque session_token (auth class 2).
   * Gateway-exempt — the JWT may already be expired.
   *
   * `unauthenticated`: the credential is the `session_token` in the BODY. This
   * is the route a degraded holder recovers through, so requiring a live bearer
   * here would make renewal impossible exactly when it is needed.
   */
  async renewSession(params: {
    session_token: string;
    session_duration_minutes?: number;
  }): Promise<ActivatedSessionResponse> {
    return this.http.post('/v1/auth/session/renew', params, { unauthenticated: true });
  }

  /**
   * OIDC discovery document. Gateway-exempt via the `/.well-known/` prefix in
   * `go/internal/api/middleware.go` `authExemptPrefixes` — a discovery document
   * that required a session would defeat its own purpose.
   */
  async getOpenIDConfig(): Promise<OIDCConfig> {
    return this.http.get('/.well-known/openid-configuration', undefined, {
      unauthenticated: true,
    });
  }

  /**
   * List the organizations the authenticated member may switch into.
   */
  async listOrganizations(): Promise<OrgMembership[]> {
    const res = await this.http.get<{ organizations: ListedOrganization[] }>(
      '/v1/auth/organizations',
    );
    return res.organizations.map((o) => ({
      organization_id: o.organization_id,
      name: o.organization_name,
      account_id: '',
    }));
  }

  /**
   * Create an organization via Discovery IST (auth class 4).
   * Caller must Activate the returned claim-less session pair.
   *
   * `unauthenticated`: the credential is the `intermediate_session_token` in the
   * BODY. The caller is mid-Discovery and has no member session yet, so there is
   * no bearer to send. NOTE the method asymmetry — `GET /v1/auth/organizations`
   * (`listOrganizations` above) is a normal authenticated read on the same path;
   * only POST is class 4.
   */
  async createOrganization(params: {
    intermediate_session_token: string;
    name: string;
    slug?: string;
    session_duration_minutes?: number;
  }): Promise<CreateOrganizationResponse> {
    return this.http.post('/v1/auth/organizations', params, { unauthenticated: true });
  }

  /**
   * Switch into another organization via Stytch sessions.Exchange, then
   * re-activate so account_id is re-resolved for the destination org.
   */
  async switchOrganization(params: {
    organization_id: string;
    account_id?: string;
    session_token?: string;
    session_duration_minutes?: number;
  }): Promise<ActivatedSessionResponse> {
    return this.http.post('/v1/auth/session/switch-organization', params);
  }

  /** Browser → create a single-use desktop handoff code bound to a daemon nonce. */
  async createDesktopHandoff(params: {
    nonce: string;
    session_token?: string;
    session_duration_minutes?: number;
  }): Promise<{ code: string; expires_at: string }> {
    return this.http.post('/v1/auth/desktop-handoff', params);
  }

  /**
   * Daemon → redeem code+nonce for a one-shot Stytch token pair (auth class 3).
   *
   * `unauthenticated`: the credential is the `code` + `nonce` pair in the BODY.
   * This is the route a daemon with NO credential at all uses to get its first
   * one — a bearer requirement here would be unsatisfiable by construction.
   */
  async redeemDesktopHandoff(params: {
    code: string;
    nonce: string;
  }): Promise<RedeemDesktopHandoffResponse> {
    return this.http.post('/v1/auth/desktop-handoff/redeem', params, {
      unauthenticated: true,
    });
  }

  /**
   * List organization members from Stytch (paginated), decorated with local
   * organization_role_grants. Cursor + limit are required on the wire contract
   * so a page is never silently truncated at the vendor default.
   */
  async listMembers(
    organizationId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<ListMembersResponse> {
    const q = new URLSearchParams();
    if (params?.cursor) q.set('cursor', params.cursor);
    // Always send an explicit limit — never omit.
    q.set('limit', String(params?.limit ?? 100));
    const qs = q.toString();
    return this.http.get(
      `/v1/auth/organizations/${encodeURIComponent(organizationId)}/members${qs ? `?${qs}` : ''}`,
    );
  }

  /**
   * Invite a member via Stytch email. Authorization is server-side
   * (organization.invite_member); Stytch rejections surface verbatim.
   */
  async inviteMember(
    organizationId: string,
    params: { email: string },
  ): Promise<InviteMemberResponse> {
    return this.http.post(
      `/v1/auth/organizations/${encodeURIComponent(organizationId)}/invitations`,
      params,
    );
  }

  /**
   * Read who may be invited to the organization. Stytch is the store — this
   * value is never mirrored locally (account ADR-012). Owner-only server-side
   * (organization.admin); a non-owner gets 403.
   */
  async getInvitePolicy(organizationId: string): Promise<InvitePolicy> {
    return this.http.get(
      `/v1/auth/organizations/${encodeURIComponent(organizationId)}/invite-policy`,
    );
  }

  /**
   * Set who may be invited. `ALL_ALLOWED` admits any address and leaves the
   * domain list stored but unenforced; `RESTRICTED` admits only the listed
   * domains and requires at least one. Disabling invites entirely is not
   * offered. Omitting `email_allowed_domains` preserves the stored list.
   * Stytch rejections (a common consumer domain such as gmail.com) surface
   * verbatim. Returns the stored policy.
   */
  async setInvitePolicy(
    organizationId: string,
    params: SetInvitePolicyRequest,
  ): Promise<InvitePolicy> {
    return this.http.put(
      `/v1/auth/organizations/${encodeURIComponent(organizationId)}/invite-policy`,
      params,
    );
  }
}

export type InviteMode = 'ALL_ALLOWED' | 'RESTRICTED';

export interface InvitePolicy {
  email_invites: InviteMode;
  email_allowed_domains: string[];
}

export interface SetInvitePolicyRequest {
  email_invites: InviteMode;
  email_allowed_domains?: string[];
}

export interface ActivatedSessionResponse {
  session_token: string;
  session_jwt: string;
  account_id: string;
  user_id: string;
  organization_id: string;
  member_id: string;
}

export interface ListedOrganization {
  organization_id: string;
  organization_name: string;
  organization_slug?: string;
}

export interface CreateOrganizationResponse {
  organization_id: string;
  account_id: string;
  user_id: string;
  member_id: string;
  session_token?: string;
  session_jwt?: string;
}

export interface RedeemDesktopHandoffResponse {
  session_token: string;
  session_jwt: string;
  account_id: string;
  user_id: string;
  organization_id: string;
  member_id: string;
}

export interface OrgMembership {
  organization_id: string;
  name: string;
  account_id: string;
}

export interface OrgMember {
  member_id: string;
  email: string;
  name?: string;
  status: string;
  user_id?: string | null;
  organization_role?: string | null;
}

export interface ListMembersResponse {
  members: OrgMember[];
  next_cursor?: string;
  total?: number;
  limit: number;
}

export interface InviteMemberResponse {
  member_id: string;
  email: string;
  status?: string;
}
